import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import ThemeProvider from './theme/ThemeProvider';
import PWAInstallPrompt from './components/pwa/PWAInstallPrompt';
import PWAUpdateNotification from './components/pwa/PWAUpdateNotification';
import OfflineIndicator from './components/pwa/OfflineIndicator';
import LoadingSpinner from './components/common/LoadingSpinner';
import { LazyDashboards, LazyPages, LazyTestPages } from './routes/LazyRoutes';
import { withLazyLoading, LoadingStates } from './utils/lazyLoading';
import { featureLoader } from './utils/dynamicFeatureLoader';
import { preloadCriticalResources, AdaptiveLoader } from './utils/bundleOptimization';
import { useRoutePreloading } from './utils/routePreloader';
import BundlePerformanceMonitor from './components/dev/BundlePerformanceMonitor';
import CodeSplittingTestPage from './components/test-pages/CodeSplittingTestPage';
import { RealtimeUpdate } from './services/websocket';
import { User } from './types/auth';
import './App.css';

// Create lazy-loaded components with proper loading states
const LazyOpsRecruiterDashboard = withLazyLoading(LazyDashboards.OpsRecruiterDashboard, LoadingStates.Page);
const LazyRecruitingManagerDashboard = withLazyLoading(LazyDashboards.RecruitingManagerDashboard, LoadingStates.Page);
const LazyCandidateDiscovery = withLazyLoading(LazyPages.CandidateDiscovery, LoadingStates.Page);
const LazyJobPosting = withLazyLoading(LazyPages.JobPosting, LoadingStates.Page);
const LazyUserManagement = withLazyLoading(LazyPages.UserManagement, LoadingStates.Page);
const LazyAgentTestPage = withLazyLoading(LazyPages.AgentTestPage, LoadingStates.Page);
const LazySearchTestPage = withLazyLoading(LazyPages.SearchTestPage, LoadingStates.Page);

// Test pages
const LazyTestPageNavigation = withLazyLoading(LazyTestPages.TestPageNavigation, LoadingStates.Page);
const LazyDesignSystemTestPage = withLazyLoading(LazyTestPages.DesignSystemTestPage, LoadingStates.Page);
const LazyDashboardTestPage = withLazyLoading(LazyTestPages.DashboardTestPage, LoadingStates.Page);
const LazyAgentInteractionTestPage = withLazyLoading(LazyTestPages.AgentInteractionTestPage, LoadingStates.Page);
const LazySearchSystemTestPage = withLazyLoading(LazyTestPages.SearchSystemTestPage, LoadingStates.Page);
const LazyCommunicationTestPage = withLazyLoading(LazyTestPages.CommunicationTestPage, LoadingStates.Page);
const LazyTouchInteractionTestPage = withLazyLoading(LazyTestPages.TouchInteractionTestPage, LoadingStates.Page);
const LazyDeviceIntegrationTestPage = withLazyLoading(LazyTestPages.DeviceIntegrationTestPage, LoadingStates.Page);
const LazyChartLibraryTestPage = withLazyLoading(LazyTestPages.ChartLibraryTestPage, LoadingStates.Page);

// Cache System Test Page
const LazyCacheSystemTestPage = React.lazy(() => import('./components/test-pages/CacheSystemTestPage'));

// Data Loading Optimization Test Page
const LazyDataLoadingOptimizationTestPage = React.lazy(() => import('./components/test-pages/DataLoadingOptimizationTestPage'));

// Performance Monitoring Test Page
const LazyPerformanceMonitoringTestPage = React.lazy(() => import('./components/test-pages/PerformanceMonitoringTestPage'));

// Dashboard Builder Test Page
const LazyDashboardBuilderTestPage = React.lazy(() => import('./components/test-pages/DashboardBuilderTestPage'));

// Data Export Test Page
const LazyDataExportTestPage = React.lazy(() => import('./components/test-pages/DataExportTestPage'));

// AI Insights Test Page
const LazyAIInsightsTestPage = React.lazy(() => import('./components/test-pages/AIInsightsTestPage'));

// Preferences Test Page
const LazyPreferencesTestPage = React.lazy(() => import('./components/test-pages/PreferencesTestPage'));

// Mock user for demo mode
const mockUser: User = {
  id: '1',
  email: 'demo@pirkus.com',
  name: 'Demo User',
  role: 'ops_recruiter',
  subscription_status: 'active',
  trial_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
};

function App() {
  const [user] = useState<User>(mockUser);
  const [realtimeUpdates, setRealtimeUpdates] = useState<RealtimeUpdate[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showPerformanceMonitor, setShowPerformanceMonitor] = useState(false);

  // Setup route preloading
  const routePreloading = useRoutePreloading();

  // Development keyboard shortcut for performance monitor
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'P') {
        setShowPerformanceMonitor(prev => !prev);
      }
    };

    if (process.env.NODE_ENV === 'development') {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, []);

  useEffect(() => {
    // Initialize app with performance optimizations
    const initializeApp = async () => {
      try {
        // Preload critical resources
        preloadCriticalResources();

        // Initialize adaptive loader
        const adaptiveLoader = AdaptiveLoader.getInstance();

        // Register features for dynamic loading
        featureLoader.registerFeature({
          name: 'advanced-analytics',
          loader: () => import('./components/analytics/AdvancedAnalytics'),
          priority: 'low',
          preloadCondition: () => user.role === 'recruiting_manager'
        });

        featureLoader.registerFeature({
          name: 'data-visualization',
          loader: () => import('./components/charts/DataVisualization'),
          priority: 'medium',
          preloadCondition: () => true
        });

        // Start preloading features based on user role and device capabilities
        await featureLoader.preloadFeatures();

        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize app:', error);
        setIsInitialized(true); // Continue anyway
      }
    };

    initializeApp();

    // Simulate some real-time updates for demo
    const mockUpdates: RealtimeUpdate[] = [
      {
        id: '1',
        entity: 'candidate',
        action: 'created',
        data: { name: 'John Doe', position: 'Senior Developer' },
        timestamp: new Date().toISOString()
      },
      {
        id: '2',
        entity: 'job',
        action: 'updated',
        data: { title: 'React Developer', status: 'active' },
        timestamp: new Date().toISOString()
      }
    ];
    setRealtimeUpdates(mockUpdates);
  }, [user.role]);

  const getDashboardComponent = () => {
    const dashboardProps = {
      realtimeUpdates,
      notifications,
      user
    };

    switch (user.role) {
      case 'recruiting_manager':
        return <LazyRecruitingManagerDashboard {...dashboardProps} />;
      case 'ops_recruiter':
      default:
        return <LazyOpsRecruiterDashboard {...dashboardProps} />;
    }
  };

  if (!isInitialized) {
    return (
      <div className="app-initializing">
        <LoadingSpinner size="large" message="Initializing application..." />
      </div>
    );
  }

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <Router>
          <Layout>
            <Suspense fallback={<LoadingStates.Page />}>
              <Routes>
                <Route path="/" element={getDashboardComponent()} />
                <Route path="/dashboard" element={getDashboardComponent()} />
                <Route path="/candidates" element={<LazyCandidateDiscovery />} />
                <Route path="/jobs" element={<LazyJobPosting />} />
                <Route path="/users" element={<LazyUserManagement />} />
                <Route path="/agents" element={<LazyAgentTestPage />} />
                <Route path="/search" element={<LazySearchTestPage />} />
                
                {/* Test Pages for Frontend UX Enhancement */}
                <Route path="/test" element={<LazyTestPageNavigation />} />
                <Route path="/test/design-system" element={<LazyDesignSystemTestPage />} />
                <Route path="/test/dashboard" element={<LazyDashboardTestPage />} />
                <Route path="/test/agents" element={<LazyAgentInteractionTestPage />} />
                <Route path="/test/search" element={<LazySearchSystemTestPage />} />
                <Route path="/test/communication" element={<LazyCommunicationTestPage />} />
                <Route path="/test/touch" element={<LazyTouchInteractionTestPage />} />
                <Route path="/test/device" element={<LazyDeviceIntegrationTestPage />} />
                <Route path="/test/charts" element={<LazyChartLibraryTestPage />} />
                <Route path="/test/code-splitting" element={<CodeSplittingTestPage />} />
                <Route path="/test/cache" element={<LazyCacheSystemTestPage />} />
                <Route path="/test/data-loading" element={<LazyDataLoadingOptimizationTestPage />} />
                <Route path="/test/performance" element={<LazyPerformanceMonitoringTestPage />} />
                <Route path="/test/dashboard-builder" element={<LazyDashboardBuilderTestPage />} />
                <Route path="/test/data-export" element={<LazyDataExportTestPage />} />
                <Route path="/test/ai-insights" element={<LazyAIInsightsTestPage />} />
                <Route path="/test/preferences" element={<LazyPreferencesTestPage />} />
                
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Layout>
          
          {/* PWA Components */}
          <OfflineIndicator />
          <PWAUpdateNotification />
          <PWAInstallPrompt />
          
          {/* Development Performance Monitor */}
          {process.env.NODE_ENV === 'development' && (
            <BundlePerformanceMonitor
              isVisible={showPerformanceMonitor}
              onClose={() => setShowPerformanceMonitor(false)}
            />
          )}
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
